import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  User,
  Building2,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Textarea } from '@/components/atoms/textarea';
import { Progress } from '@/components/atoms/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from '@/components/molecules/form-field';

import { useCompleteOnboarding } from '@/features/onboarding/hooks/use-complete-onboarding';
import { useProfile } from '@/features/settings/hooks/use-profile';
import { useCompany } from '@/features/settings/hooks/use-company';
import { useCountries } from '@/lib/hooks/use-countries';

// Form schemas
const profileSchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
  phone: z.string().min(8, 'Le numéro de téléphone est requis'),
  address_line1: z.string().min(3, "L'adresse est requise"),
  city: z.string().min(2, 'La ville est requise'),
  countryIsoCode3: z.string().min(3, 'Le pays est requis'),
  postal_code: z.string().optional(),
  landmark: z.string().optional(),
});

const companySchema = z.object({
  companyName: z.string().min(2, "Le nom de l'entreprise est requis"),
  companyPhone: z.string().min(8, 'Le téléphone professionnel est requis'),
  companyEmail: z.string().email('Email invalide'),
  taxId: z.string().optional(),
  description: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;
type CompanyFormData = z.infer<typeof companySchema>;

const STEPS = [
  { id: 'profile', icon: User, labelKey: 'onboarding.steps.profile' },
  { id: 'company', icon: Building2, labelKey: 'onboarding.steps.company' },
  { id: 'complete', icon: CheckCircle, labelKey: 'onboarding.steps.complete' },
];

export default function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const { data: profile, isLoading: isLoadingProfile } = useProfile();
  const { data: company, isLoading: isLoadingCompany } = useCompany();
  const { data: countries } = useCountries();
  const { mutate: completeOnboarding, isPending: isSubmitting } = useCompleteOnboarding();

  // Profile form
  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: '',
      phone: '',
      address_line1: '',
      city: '',
      countryIsoCode3: 'COM',
      postal_code: '',
      landmark: '',
    },
  });

  // Company form
  const companyForm = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      companyName: '',
      companyPhone: '',
      companyEmail: '',
      taxId: '',
      description: '',
    },
  });

  // Populate forms with existing data
  useEffect(() => {
    if (profile) {
      profileForm.reset({
        name: profile.name || '',
        phone: profile.address?.phone_number || '',
        address_line1: profile.address?.address_line1 || '',
        city: profile.address?.city || '',
        countryIsoCode3: profile.address?.country?.isoCode3 || 'COM',
        postal_code: profile.address?.postal_code || '',
        landmark: profile.address?.landmark || '',
      });
    }
  }, [profile, profileForm]);

  useEffect(() => {
    if (company) {
      companyForm.reset({
        companyName: company.name || '',
        companyPhone: company.phone || '',
        companyEmail: company.email || '',
        taxId: company.taxId || '',
        description: company.description || '',
      });
    }
  }, [company, companyForm]);

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const handleProfileNext = async (_data: ProfileFormData) => {
    // Store profile data and move to next step
    setCurrentStep(1);
  };

  const handleCompanyNext = async (data: CompanyFormData) => {
    const profileData = profileForm.getValues();
    const selectedCountry = countries?.find((c) => c.isoCode3 === profileData.countryIsoCode3);

    completeOnboarding(
      {
        profile: {
          name: profileData.name,
          phone: profileData.phone,
          address: {
            address_line1: profileData.address_line1,
            city: profileData.city,
            countryIsoCode3: profileData.countryIsoCode3,
            countryName: selectedCountry?.name || 'Comoros',
            postal_code: profileData.postal_code,
            landmark: profileData.landmark,
          },
        },
        company: {
          name: data.companyName,
          phone: data.companyPhone,
          email: data.companyEmail,
          taxId: data.taxId,
          description: data.description,
        },
      },
      {
        onSuccess: () => {
          setCurrentStep(2);
          toast.success(t('onboarding.success'));
        },
        onError: (error) => {
          console.error('Onboarding error:', error);
          toast.error(t('onboarding.error'));
        },
      }
    );
  };

  const handleComplete = () => {
    navigate('/');
  };

  if (isLoadingProfile || isLoadingCompany) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">{t('onboarding.title')}</h1>
          <p className="text-muted-foreground mt-2">{t('onboarding.subtitle')}</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-2 ${
                    isActive ? 'text-primary' : isCompleted ? 'text-green-600' : 'text-muted-foreground'
                  }`}
                >
                  <div
                    className={`size-8 rounded-full flex items-center justify-center ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : isCompleted
                        ? 'bg-green-600 text-white'
                        : 'bg-muted'
                    }`}
                  >
                    {isCompleted ? <CheckCircle className="size-4" /> : <Icon className="size-4" />}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">
                    {t(step.labelKey)}
                  </span>
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Content */}
        {currentStep === 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="size-5" />
                {t('onboarding.profile.title')}
              </CardTitle>
              <CardDescription>{t('onboarding.profile.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...profileForm}>
                <form onSubmit={profileForm.handleSubmit(handleProfileNext)} className="space-y-4">
                  <FormField
                    control={profileForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('onboarding.profile.name')} *</FormLabel>
                        <FormControl>
                          <Input placeholder={t('onboarding.profile.namePlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('onboarding.profile.phone')} *</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="+269 XXX XX XX" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={profileForm.control}
                      name="countryIsoCode3"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboarding.profile.country')} *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t('onboarding.profile.selectCountry')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {countries?.map((country) => (
                                <SelectItem key={country.isoCode3} value={country.isoCode3}>
                                  {country.flagEmoji} {country.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={profileForm.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboarding.profile.city')} *</FormLabel>
                          <FormControl>
                            <Input placeholder={t('onboarding.profile.cityPlaceholder')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={profileForm.control}
                    name="address_line1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('onboarding.profile.address')} *</FormLabel>
                        <FormControl>
                          <Input placeholder={t('onboarding.profile.addressPlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="landmark"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('onboarding.profile.landmark')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('onboarding.profile.landmarkPlaceholder')} {...field} />
                        </FormControl>
                        <FormDescription>{t('onboarding.profile.landmarkHint')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end pt-4">
                    <Button type="submit">
                      {t('onboarding.next')}
                      <ArrowRight className="ml-2 size-4" />
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}

        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="size-5" />
                {t('onboarding.company.title')}
              </CardTitle>
              <CardDescription>{t('onboarding.company.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...companyForm}>
                <form onSubmit={companyForm.handleSubmit(handleCompanyNext)} className="space-y-4">
                  <FormField
                    control={companyForm.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('onboarding.company.name')} *</FormLabel>
                        <FormControl>
                          <Input placeholder={t('onboarding.company.namePlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={companyForm.control}
                      name="companyPhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboarding.company.phone')} *</FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="+269 XXX XX XX" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={companyForm.control}
                      name="companyEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboarding.company.email')} *</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="contact@entreprise.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={companyForm.control}
                    name="taxId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('onboarding.company.taxId')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('onboarding.company.taxIdPlaceholder')} {...field} />
                        </FormControl>
                        <FormDescription>{t('onboarding.company.taxIdHint')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={companyForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('onboarding.company.description')}</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t('onboarding.company.descriptionPlaceholder')}
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-between pt-4">
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(0)}>
                      <ArrowLeft className="mr-2 size-4" />
                      {t('onboarding.back')}
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
                      {t('onboarding.complete')}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}

        {currentStep === 2 && (
          <Card>
            <CardContent className="pt-8 pb-8 text-center">
              <div className="size-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="size-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t('onboarding.completed.title')}</h2>
              <p className="text-muted-foreground mb-6">{t('onboarding.completed.description')}</p>
              <Button onClick={handleComplete} size="lg">
                {t('onboarding.completed.goToDashboard')}
                <ArrowRight className="ml-2 size-4" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
